import { MultiSelectOption } from '@dispatch/shared/types';
import { Control, FieldErrors } from 'react-hook-form';
import {
  AsyncSelect,
  DateRangeField,
  SelectField,
  TextField,
  ToggleField,
} from '../fields';
import { Filter } from '../page-component';

export const inputLookUp = {
  date: TextField,
  text: TextField,
  'date-range': DateRangeField,
  select: SelectField,
  'async-select': AsyncSelect,
  toggle: ToggleField,
};

type BuildFilterProps = {
  label: string;
  data: Filter[];
}[];

export const buildFilterLookup = (filters: BuildFilterProps) => {
  const lookup: any = {};

  filters
    ?.flatMap((f) => f.data)
    .forEach((f: Filter) => {
      const buildObject: Filter & { optionLookup?: Record<string, any> } = f;
      if (
        buildObject?.conditionalRender &&
        buildObject?.conditionalRender?.id
      ) {
        lookup[buildObject?.conditionalRender?.id] =
          buildObject?.conditionalRender;
        if (buildObject?.conditionalRender?.options) {
          // should change to input opiton lookup;
          lookup[buildObject?.conditionalRender?.id].optionLookup =
            Object.fromEntries(
              (buildObject?.conditionalRender?.options || []).map(
                (o: { value: string | boolean | number; title: string }) => [
                  o.value,
                  o.title,
                ]
              )
            );
        }
      }

      if (buildObject?.options) {
        buildObject.optionLookup = Object.fromEntries(
          (buildObject?.options || []).map(
            (o: { value: string | boolean | number; title: string }) => [
              o.value,
              o.title,
            ]
          )
        );
      }

      lookup[f.id] = buildObject;
    });
  return lookup;
};

export const getFilterProps = (
  filter: Filter,
  key: number | string,
  control: Control,
  errors: FieldErrors
) => {
  let props: any = {
    key: `${key}_${filter.type}`,
    id: filter.id,
    name: filter.name,
    label: filter.label,
    control: control,
    errors: errors,
  };

  if (filter?.type === 'date') {
    props.type = 'date';
    props.inputLabelProps = {
      shrink: true,
    };
  }

  if (filter?.type === 'date-range') {
    props = {
      ...props,
      format: 'YYYY/dd/MM',
      startLabel: filter?.start,
      endLabel: filter?.end,
    };
  }

  if (filter?.type === 'select') {
    props = {
      ...props,
      options: (filter?.options as MultiSelectOption[]) || [],
      onValueChange: filter?.onChange,
    };
  }

  if (filter?.type === 'toggle') {
    props = {
      ...props,
      buttons: filter?.buttons || [],
      onValueChange: filter?.onChange,
    };
  }

  if (filter?.multi) {
    props = {
      ...props,
      multiple: true,
      limitTags: 1,
      options: (filter?.options as MultiSelectOption[]) || [],
    };
  }

  if (filter.type === 'async-select') {
    props = {
      ...props,
      cancel: filter?.cancel,
      setInput: filter?.setInput,
      loading: filter?.loading,
      limitTags: 25,
      defaultValue: '',
      tagLimit: filter?.tagLimit,
    };
  }

  if (filter?.required) {
    props = {
      ...props,
      required: true,
    };
  }

  return props;
};

export const handleDateRange = (
  values: any,
  filter: Filter[][],
  field: Filter[],
  setValue: (key: string, value: unknown) => void
) => {
  values[`${field?.[0].start}`] = filter[1][0];
  values[`${field?.[0].end}`] = filter[1][1];
  if (field?.[0].start) {
    setValue(field?.[0].start, filter[1][0]);
  }
  if (field?.[0].end) {
    setValue(field?.[0].end, filter[1][1]);
  }
};

// Normalize a single saved-filter entry for POSTing to the persistence layer.
// Arrays are JSON.stringified by default (legacy string-blob convention), with
// two opt-outs:
//   - `type: 'date-range'` (preserves the [start, end] tuple)
//   - `persistAsArray: true` (preserves the native array so the backend can
//     bind directly to `string[]?`, e.g. resourceSelectedAttributeGroups)
// `resourceAttributes` is always stringified regardless of array-ness because
// its persisted form is a string blob.
export const normalizeFilterValueForPersistence = (
  entry: [string, unknown],
  filterLookUp: Record<string, Filter>
): [string, unknown] | [] => {
  const [key, value] = entry;

  if (key === 'resourceAttributes') {
    return [key, JSON.stringify(value)];
  }

  if (value === '') {
    return [];
  }

  if (Array.isArray(value)) {
    const field = filterLookUp[key];
    const isDateRange =
      field?.type === 'date-range' && value.length > 0 && Boolean(field?.start);
    const persistAsArray = field?.persistAsArray === true;

    if (isDateRange || persistAsArray) {
      return [key, value];
    }
    return [key, JSON.stringify(value)];
  }

  return [key, value];
};

export const cleanFilters = (
  array: [string, unknown][],
  filterLookUp: Record<string, unknown>
) => {
  const clearEmptyFilters = array.map((currFilter) => {
    const filter = currFilter;

    if (typeof filter[1] === 'string' && filter[1].includes('[')) {
      filter[1] = JSON.parse(filter[1]);
    }

    if (filter[1] !== '') {
      if (Array.isArray(filter[1]) && filter[1]?.length > 0) {
        const field: [Filter, unknown] = filterLookUp[filter[0]] as [
          Filter,
          unknown
        ];

        if (field?.[0]?.type === 'date-range' && filter[1]?.length > 0) {
          return filter;
        }
      }
      return filter;
    }
    return [];
  });

  return clearEmptyFilters.filter((item) => item[0] !== undefined);
};
